import { Filter, HorseshoeConfig } from '../shared/types/podlet';
import {
    BannerGlobal,
    Finn,
    Keywords,
    PageType,
    Subvertical,
    Vertical,
    XandrData,
} from '../shared/types/ast';
import {
    markBannerRefresh,
    markFilterUpdated,
    markPageLoaded,
    markSetPageOpts,
} from './utils/performance';
import { publish, subscribe } from './podlet/events';
import { copyQueue } from './stories/helper';
import { verticalSpesificUpdate } from './podlet/vertical';
import { Feed, ReklameConfiguration } from '../shared/types/reklameMaster';
import { getGlimrKeywords } from './utils/glimr';
import { filterKeywordsLegacy } from '../shared/filter';
import { nrmFeedToString } from '../shared/utils';
import { deepEqual, getKeywordsAsStrings, isEmpty } from '../shared/utils';
import { getSearchReklameConfig } from '../shared/services/reklame-master-provider/reklame-master-provider';
import { setExternalUid } from './utils/external-id';
import { displayAdsLog } from './utils/debug';
import { preProcessTravelFilter } from '../shared/query-requests';

class RefreshState {
    private _currentFilter: Filter | undefined;
    private _currentPageType: PageType | undefined;
    private _currentVertical: Vertical | undefined;
    private _currentFilterKeywords = {};
    private _currentSubvertical: Subvertical | undefined;
    private _keywords: Keywords;
    private _baseKeywords: Keywords;
    private _xandr: XandrData;
    private _clientReady = false;
    private _cycle = 0;

    get currentFilter(): Filter | undefined {
        return this._currentFilter;
    }

    set currentFilter(value: Filter | undefined) {
        this._currentFilter = value;
    }

    get currentFilterKeywords(): {} {
        return this._currentFilterKeywords;
    }

    set currentFilterKeywords(value: {}) {
        this._currentFilterKeywords = value;
    }

    get currentPageType(): PageType | undefined {
        return this._currentPageType;
    }

    set currentPageType(value: PageType | undefined) {
        this._currentPageType = value;
    }

    get currentVertical(): Vertical | undefined {
        return this._currentVertical;
    }

    set currentVertical(value: Vertical | undefined) {
        this._currentVertical = value;
    }
    get currentSubvertical(): Subvertical | undefined {
        return this._currentSubvertical;
    }

    set currentSubvertical(value: Subvertical | undefined) {
        this._currentSubvertical = value;
    }

    get xandr(): XandrData {
        return this._xandr;
    }

    set xandr(value: XandrData) {
        this._xandr = value;
    }

    get keywords(): Keywords {
        return this._keywords;
    }

    set keywords(value: Keywords) {
        this._keywords = value;
    }

    get baseKeywords(): Keywords {
        return this._baseKeywords;
    }

    set baseKeywords(value: Keywords) {
        this._baseKeywords = value;
    }

    get clientReady(): boolean {
        return this._clientReady;
    }

    set clientReady(value: boolean) {
        this._clientReady = value;
    }

    get cycle(): number {
        return this._cycle;
    }

    set cycle(value: number) {
        this._cycle = value;
    }

    /*
    Legacy handling of command based event
     */
    updateKeywords(filter: Filter) {
        const update = filterKeywordsLegacy(
            this.currentVertical,
            filter,
            this.currentFilter,
        );
        this.currentFilter = filter;

        if (update.keywords) {
            this.keywords = { ...this.keywords, ...update.keywords };
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.setPageOpts({
                            keywords: this.keywords,
                        });
                    }
                });
            }
        }

        if (update.refresh) {
            if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                    if (window.apntag) {
                        window.apntag.refresh();
                    }
                });
            }
        }
    }

    pageChange(existingFilter?: Filter, updateFilter?: Filter) {
        if (updateFilter?.pageType) {
            return true;
        }
        return (
            existingFilter?.page !== updateFilter?.page ||
            existingFilter?.side !== updateFilter?.side
        );
    }

    shouldRefresh(vertical: string, keywordDiff = false) {
        switch (vertical) {
            case 'car':
            case 'bap':
                return keywordDiff;
            default:
                return true;
        }
    }

    updateFeed(nrmFeed: Feed) {
        const nrmFeedString = nrmFeedToString(nrmFeed);

        window.FINN = window.FINN || ({} as Finn);
        window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
        window.FINN.banner.feed = nrmFeed;
        window.FINN.banner.feedString = nrmFeedString;
    }

    async getSearchReklameConfigFromApi(
        filter: Filter,
        config: HorseshoeConfig,
    ): Promise<ReklameConfiguration | null> {
        const { baseUrl, deviceType } = config;

        if (isEmpty(filter)) {
            return null;
        } else {
            displayAdsLog(
                `getSearchReklameConfig() ${JSON.stringify(filter)} `,
            );
            return await getSearchReklameConfig(deviceType, filter, baseUrl);
        }
    }

    configUpdated(updatedConfig: Filter, config: HorseshoeConfig) {
        if (this.keywords) {
            // Some travel subverticals don't know who they are before the client is loaded
            if (config.vertical && updatedConfig.subvertical) {
                this.keywords[
                    'no-sno-finn-subsection'
                ] = `${config.vertical}_${updatedConfig.subvertical}`;
                this.currentSubvertical =
                    updatedConfig.subvertical as Subvertical;
            }
            if (updatedConfig.pageType) {
                this.keywords['aa-sch-page_type'] =
                    updatedConfig.pageType as string;
            }
        }
    }

    refreshBanners() {
        displayAdsLog(`refreshBanners()`);
        if (this.clientReady) {
            this.cycle = this.cycle + 1;
            markBannerRefresh();

            // publish to ad.ts
            publish('banner', 'refresh', this.cycle);
        } else {
            this.clientReady = true;
            copyQueue();
        }
    }

    getXandrRequestData() {
        return { ...this.xandr, ...this.xandr.pageOpts };
    }

    setInitialData(config: HorseshoeConfig) {
        if (config) {
            const { xandr, filter, pageType, subvertical, vertical } = config;
            this.currentPageType = pageType;
            this.currentSubvertical = subvertical;
            this.currentVertical = vertical;
            this.currentFilter = getFilterData(filter);
            this.baseKeywords = xandr.pageOpts.keywords;
            this.keywords = {
                ...xandr.pageOpts.keywords,
                ...getGlimrKeywords(),
            };
            const pageOpts = {
                ...xandr.pageOpts,
                keywords: this.keywords,
            };
            const pageOptsWithUserData = setExternalUid(pageOpts);
            this.xandr = {
                ...xandr,
                pageOpts: pageOptsWithUserData,
            };
        }
    }

    setupSubscriptions(config) {
        // TODO: Is this important?
        subscribe('banner', 'update', (event) => {
            this.updateKeywords(event.payload as Filter);
        });

        subscribe('filter', 'updated', (event) => {
            const updateOnChangeForVertical = config.vertical === 'travel';
            if (updateOnChangeForVertical) {
                markFilterUpdated();
                this.filterUpdated(event.payload as Filter, config);
            }
        });

        /**
         * Used to set some keywords from published events on load, e.g. on travel:
         *  const payload = {
         *             pageType,
         *             side: page || 1,
         *             destinasjon: {
         *                 by: city || '',
         *                 land: country || '',
         *             },
         *         };
         */
        subscribe('page', 'loaded', (event) => {
            markPageLoaded();
            this.configUpdated(event.payload as Filter, config);
            this.filterUpdated(event.payload as Filter, config);
        });
    }

    /**
     * This is the only function called when a filter is updated
     * @param updatedFilter
     * @param config
     */
    async filterUpdated(updatedFilter: Filter, config: HorseshoeConfig) {
        const { vertical, subvertical } = config;

        verticalSpesificUpdate(config, vertical, this.currentSubvertical);

        displayAdsLog(
            `filter before verticalSpesificPreProccessing() ${JSON.stringify(
                updatedFilter,
            )}`,
        );
        const filter = preProcessTravelFilter(
            updatedFilter,
            vertical,
            subvertical,
        );
        displayAdsLog(
            `filter after verticalSpesificPreProccessing() ${JSON.stringify(
                filter,
            )}`,
        );

        const pageType = filter.pageType as PageType;

        // TODO: Is this important?
        if (pageType && pageType !== this.currentPageType) {
            filter['pagetype'] = [pageType];
            this.currentPageType = pageType;
        }

        const reklameConfig =
            subvertical === 'prepackage'
                ? ({} as ReklameConfiguration)
                : await this.getSearchReklameConfigFromApi(filter, config);

        displayAdsLog(`reklameConfig ${JSON.stringify(reklameConfig)}`);

        const nrmKeywords = getKeywordsAsStrings(reklameConfig?.keywords);
        displayAdsLog(`nrmKeywords ${JSON.stringify(nrmKeywords)}`);

        const keywordDiff = deepEqual(nrmKeywords, this.baseKeywords);
        displayAdsLog(`keywordDiff ${JSON.stringify(keywordDiff)}`);

        // Figure out if ads should be refresh
        const alwaysRefresh = this.shouldRefresh(vertical, keywordDiff);
        const isPageChange = this.pageChange(this.currentFilter, filter);
        displayAdsLog(
            `alwaysRefresh ${alwaysRefresh}, isPageChange ${isPageChange}`,
        );
        const refresh = alwaysRefresh || isPageChange;

        let nrmFeed = window.FINN.banner.feed as Feed;

        this.currentFilter = filter;
        // this.currentFilterKeywords = keywordsUpdate;

        if (reklameConfig?.feed) {
            nrmFeed = reklameConfig?.feed;
        }

        this.keywords = {
            ...this.keywords,
            ...nrmKeywords,
        };
        displayAdsLog(`Should refresh: ${refresh}`);
        if (refresh) {
            if (window.apntag && window.apntag.anq) {
                //@ts-ignore
                window.apntag.anq.push(() => {
                    displayAdsLog(
                        `setPageOpts() ${JSON.stringify(this.keywords)}`,
                    );

                    if (window.apntag) {
                        markSetPageOpts();
                        window.apntag.setPageOpts({
                            keywords: this.keywords,
                        });
                    }
                });
                displayAdsLog(`this.refreshBanners`);
                this.refreshBanners();
            }
        }

        this.updateFeed(nrmFeed);
    }
}

function getFilterData(filterData: Filter) {
    if (filterData) {
        if (filterData.page) {
            filterData.page = Number(filterData.page);
        }
        if (filterData.side) {
            filterData.side = Number(filterData.side);
        }
    }
    return filterData;
}

let refreshState;

export function getRefreshState(): RefreshState {
    if (refreshState instanceof RefreshState) {
        return refreshState;
    } else {
        refreshState = new RefreshState();
        return refreshState;
    }
}
